import React, { useState, useEffect } from 'react';
import supabase from './supabaseClient';

interface SocialPrivacySettingsProps {
  telegramUserId: string;
  clientName: string;
  onSettingsUpdated: () => void;
}

interface ClientProfile {
  social_privacy_setting: 'initials' | 'anonymous' | 'full_name';
  allow_social_comparison: boolean;
  display_name?: string;
}

export default function SocialPrivacySettings({ 
  telegramUserId, 
  clientName, 
  onSettingsUpdated 
}: SocialPrivacySettingsProps) {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [profile, setProfile] = useState<ClientProfile>({
    social_privacy_setting: 'initials',
    allow_social_comparison: true,
    display_name: clientName
  });

  useEffect(() => {
    loadClientProfile();
  }, [telegramUserId]);

  const loadClientProfile = async () => {
    try {
      setLoading(true);

      const { data, error } = await supabase
        .from('client_profiles')
        .select('social_privacy_setting, allow_social_comparison, display_name')
        .eq('telegram_user_id', telegramUserId)
        .single();

      if (error && error.code !== 'PGRST116') {
        console.error('Error loading client profile:', error);
      }

      if (data) {
        setProfile({
          social_privacy_setting: data.social_privacy_setting || 'initials',
          allow_social_comparison: data.allow_social_comparison ?? true,
          display_name: data.display_name || clientName
        });
      }
    } catch (error) {
      console.error('Error loading client profile:', error);
    } finally {
      setLoading(false);
    }
  };

  const saveSettings = async () => {
    try {
      setSaving(true);

      // First, check if a record already exists
      const { data: existingRecord, error: checkError } = await supabase
        .from('client_profiles')
        .select('id')
        .eq('telegram_user_id', telegramUserId)
        .single();

      if (checkError && checkError.code !== 'PGRST116') {
        throw checkError;
      }

      let error;
      if (existingRecord) {
                 // Update existing record
         const { error: updateError } = await supabase
           .from('client_profiles')
           .update({
             social_privacy_setting: profile.social_privacy_setting,
             allow_social_comparison: profile.allow_social_comparison,
             display_name: profile.display_name
           })
           .eq('telegram_user_id', telegramUserId);
        error = updateError;
      } else {
                 // Insert new record
         const { error: insertError } = await supabase
           .from('client_profiles')
           .insert({
             telegram_user_id: telegramUserId,
             social_privacy_setting: profile.social_privacy_setting,
             allow_social_comparison: profile.allow_social_comparison,
             display_name: profile.display_name
           });
        error = insertError;
      }

      if (error) {
        throw error;
      }

                    // Also update the trainer's client display name so it reflects across the app
       try {
         const { data: authData } = await supabase.auth.getUser();
         const trainerId = authData?.user?.id;
         if (trainerId && (profile.display_name || clientName)) {
           console.log('Attempting to update trainers_clients:', {
             trainerId,
             telegramUserId,
             newClientName: profile.display_name || clientName
           });
           
           // First check if the record exists
           const { data: existingRecord, error: checkError } = await supabase
             .from('trainers_clients')
             .select('id')
             .eq('trainer_id', trainerId)
             .eq('telegram_user_id', telegramUserId)
             .single();
           
           if (checkError && checkError.code !== 'PGRST116') {
             console.error('Error checking existing record:', checkError);
             throw checkError;
           }
           
           let updateError;
           if (existingRecord) {
             // Update existing record
             const { error } = await supabase
               .from('trainers_clients')
               .update({ client_name: profile.display_name || clientName })
               .eq('id', existingRecord.id);
             updateError = error;
           } else {
             // Insert new record
             const { error } = await supabase
               .from('trainers_clients')
               .insert({
                 trainer_id: trainerId,
                 telegram_user_id: telegramUserId,
                 client_name: profile.display_name || clientName
               });
             updateError = error;
           }
           
           if (updateError) {
             console.error('Error updating/inserting trainers_clients:', updateError);
             throw updateError;
           }
           
           console.log('Successfully updated trainers_clients');
         }
       } catch (nameSyncError) {
         console.error('Name sync error:', nameSyncError);
         // Non-fatal: UI will still use display_name where applicable
       }

      onSettingsUpdated();
      alert('Privacy settings updated successfully! 🔒');

    } catch (error: any) {
      console.error('Error saving privacy settings:', error);
      alert('Error saving privacy settings. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const getPrivacyPreview = () => {
    const displayName = profile.display_name || clientName;
    
    switch (profile.social_privacy_setting) {
      case 'anonymous':
        return 'Anonymous Client';
      case 'initials':
        const initials = displayName
          .split(' ')
          .map(word => word.charAt(0))
          .join('')
          .toUpperCase()
          .slice(0, 2);
        return `${initials}.`;
      case 'full_name':
        return displayName;
      default:
        return 'Unknown';
    }
  };

  if (loading) {
    return (
      <div className="retro-card p-4">
        <div className="index-tab">
          <h3 className="text-lg font-semibold text-[#1f1f1f]">🔒 Social Privacy Settings</h3>
        </div>
        <div className="p-4">
          <div className="text-center py-4 text-[#2d2d2d]">Loading privacy settings...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="retro-card p-4">
      <div className="index-tab">
        <h3 className="text-lg font-semibold text-[#1f1f1f]">🔒 Social Privacy Settings</h3>
      </div>
      
      <div className="p-4 space-y-6">
        {/* Social Comparison Toggle */}
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <div>
              <h4 className="font-semibold text-[#1f1f1f]">Enable Social Comparison</h4>
              <p className="text-sm text-[#666]">
                Allow this client to participate in anonymized social comparisons
              </p>
            </div>
            <label className="relative inline-flex items-center cursor-pointer">
              <input
                type="checkbox"
                checked={profile.allow_social_comparison}
                onChange={(e) => setProfile({
                  ...profile,
                  allow_social_comparison: e.target.checked
                })}
                className="sr-only peer"
              />
              <div className="w-11 h-6 bg-[#d4d4d4] peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-[#0eeaff]"></div>
            </label>
          </div>
        </div>

        {/* Display Name */}
        <div className="space-y-3">
          <label className="block text-sm font-medium text-[#1f1f1f]">
            Display Name
          </label>
          <input
            type="text"
            value={profile.display_name || ''}
            onChange={(e) => setProfile({
              ...profile,
              display_name: e.target.value
            })}
            placeholder="Enter display name"
            className="vintage-input w-full"
          />
          <p className="text-xs text-[#666]">
            This name will be used for generating initials and full name display
          </p>
        </div>

        {/* Privacy Level Selection */}
        <div className="space-y-3">
          <label className="block text-sm font-medium text-[#1f1f1f]">
            Privacy Level in Social Comparisons
          </label>
          
          <div className="space-y-2">
            <label className="flex items-center space-x-3 cursor-pointer">
              <input
                type="radio"
                name="privacy"
                value="initials"
                checked={profile.social_privacy_setting === 'initials'}
                onChange={(e) => setProfile({
                  ...profile,
                  social_privacy_setting: e.target.value as 'initials'
                })}
                className="text-[#0eeaff] focus:ring-[#0eeaff]"
              />
              <div className="flex-1">
                <div className="font-medium text-[#1f1f1f]">Show Initials</div>
                <div className="text-sm text-[#666]">
                  Display as "{getPrivacyPreview()}" in leaderboards
                </div>
              </div>
            </label>

            <label className="flex items-center space-x-3 cursor-pointer">
              <input
                type="radio"
                name="privacy"
                value="anonymous"
                checked={profile.social_privacy_setting === 'anonymous'}
                onChange={(e) => setProfile({
                  ...profile,
                  social_privacy_setting: e.target.value as 'anonymous'
                })}
                className="text-[#0eeaff] focus:ring-[#0eeaff]"
              />
              <div className="flex-1">
                <div className="font-medium text-[#1f1f1f]">Anonymous</div>
                <div className="text-sm text-[#666]">
                  Display as "Anonymous Client" in leaderboards
                </div>
              </div>
            </label>

            <label className="flex items-center space-x-3 cursor-pointer">
              <input
                type="radio"
                name="privacy"
                value="full_name"
                checked={profile.social_privacy_setting === 'full_name'}
                onChange={(e) => setProfile({
                  ...profile,
                  social_privacy_setting: e.target.value as 'full_name'
                })}
                className="text-[#0eeaff] focus:ring-[#0eeaff]"
              />
              <div className="flex-1">
                <div className="font-medium text-[#1f1f1f]">Show Full Name</div>
                <div className="text-sm text-[#666]">
                  Display as "{getPrivacyPreview()}" in leaderboards
                </div>
              </div>
            </label>
          </div>
        </div>

        {/* Preview */}
        <div className="bg-[#f8f4e6] border border-[#d4d4d4] rounded-lg p-4">
          <h5 className="font-semibold text-[#1f1f1f] mb-2">Preview</h5>
          <div className="flex items-center space-x-3">
            <div className="w-12 h-12 bg-gradient-to-br from-[#0eeaff] to-[#8aff8a] rounded-full flex items-center justify-center text-white font-bold">
              {profile.display_name?.charAt(0) || 'U'}
            </div>
            <div>
              <div className="font-medium text-[#1f1f1f]">{getPrivacyPreview()}</div>
              <div className="text-sm text-[#666]">85% success • 7 day streak 🔥</div>
            </div>
          </div>
        </div>

        {/* Save Button */}
        <div className="flex justify-end">
          <button
            onClick={saveSettings}
            disabled={saving}
            className={`neon-button ${
              saving ? 'opacity-50 cursor-not-allowed' : 'hover:scale-105'
            }`}
          >
            {saving ? '💾 Saving...' : '💾 Save Settings'}
          </button>
        </div>

        {/* Privacy Note */}
        <div className="bg-[#ffd700]/10 border border-[#ffd700] rounded-lg p-3">
          <p className="text-xs text-[#2d2d2d]">
            <strong>Privacy Note:</strong> These settings only apply to social comparison features. 
            Your trainer will always see your full information for coaching purposes.
          </p>
        </div>
      </div>
    </div>
  );
} 